/**
 * @file Builds the U.S.-person, residence-state and SSN inputs for an
 * investment profile.
 */
import { Alert } from "antd";
import type { RadioChangeEvent } from "antd";

import { W1 } from "@components/Constants";
import { US_STATE_OPTIONS } from "@components/Address";
import { Disclaimer, Markdown } from "@components/Text";
import { TYPE_BOOLEAN, TYPE_STRING, TYPE_NONE } from "@components/Form";

import { getNoEinAlert, TITLE_WARNING } from "./getProfileEntityOtherSchema";
import type {
  Input,
  UpdateSchema,
  ResidencyLabels,
  ResidencyProfile,
} from "../types";

const TOOLTIP_NON_US_PERSON = `
For federal tax purposes, you are considered a U.S. person if you are:
An individual who is a U.S. citizen or U.S. resident alien;
A partnership, corporation, company, or association created or organized in the United States or under the laws of the United States;
An estate (other than a foreign estate); or
A domestic trust (as defined in Regulations section 301.7701-7).
`;

const LABEL_HAS_OTHER_US_RESIDENCE_STATE = `
        Is the state listed under your address also your state of residence
        for tax purposes?
      `;
const LABEL_RESIDENCE_STATE = "Please list your state of residence";
const LABEL_HAS_SSN = "Do you have an SSN?";

const DEFAULT_LABELS: ResidencyLabels = { isUsPerson: "" };

const ALERT_STYLE = { marginTop: W1 };
const STATE_INPUT_STYLE = { width: 180 };

/**
 * Builds the U.S.-person, residence-state and SSN inputs, showing or
 * hiding each one based on the profile's current citizenship answers.
 */
const computeResidencyInputs = (
  profile: ResidencyProfile,
  updateSchema: UpdateSchema,
  labels: ResidencyLabels = DEFAULT_LABELS,
  organizationEmail = "",
): Input[] => {
  const { onIsUsPersonChange } = updateSchema;
  const noSsnAlertMessage = getNoEinAlert(organizationEmail);

  const typesMap = {
    hasOtherUsResidenceState: TYPE_NONE,
    isUsPerson: TYPE_NONE,
    residenceState: TYPE_NONE,
    hasSsn: TYPE_NONE,
    hasEinNumber: TYPE_NONE,
  };

  const { isUsPerson, hasOtherUsResidenceState, isUsCitizen, hasSsn } = profile;
  const isNotUsCitizen = isUsCitizen === false;
  const showResidenceState = (isUsCitizen && hasOtherUsResidenceState) || (isNotUsCitizen && isUsPerson && hasOtherUsResidenceState);
  const showHasOtherUsResidenceState = isUsCitizen || (isNotUsCitizen && isUsPerson);

  if (isUsCitizen) {
    typesMap.hasOtherUsResidenceState = TYPE_BOOLEAN;

    if (hasOtherUsResidenceState) {
      typesMap.residenceState = TYPE_STRING;
    }
  }

  if (isNotUsCitizen) {
    typesMap.isUsPerson = TYPE_BOOLEAN;
  } else {
    typesMap.isUsPerson = TYPE_NONE;
  }

  if (showHasOtherUsResidenceState) {
    typesMap.hasOtherUsResidenceState = TYPE_BOOLEAN;
  }

  if (showResidenceState) {
    typesMap.residenceState = TYPE_STRING;
  } else {
    typesMap.residenceState = TYPE_NONE;
  }

  const statesInputs: Input[] = [
    {
      name: "hasOtherUsResidenceState",
      type: typesMap.hasOtherUsResidenceState,
      required: true,
      /* td: we can include state to make it more clear */
      label: labels.hasOtherUsResidenceState || LABEL_HAS_OTHER_US_RESIDENCE_STATE,
      inputProps: {
        hasInvertedValues: true,
        /** Refreshes the schema after the residence-state answer changes. */
        onChange: () => updateSchema(),
      },
    },
    {
      name: "residenceState",
      type: typesMap.residenceState,
      label: labels.residenceState || LABEL_RESIDENCE_STATE,
      options: US_STATE_OPTIONS,
      required: true,
      placeholder: "",
      inputProps: {
        style: STATE_INPUT_STYLE,
      },
    },
  ];

  const doYouHaveSsnExtra = hasSsn === false
    ? (
      <Alert
        showIcon
        type="warning"
        style={ALERT_STYLE}
        message={TITLE_WARNING}
        description={<Markdown text={noSsnAlertMessage} />}
      />
    )
    : undefined;

  const isUsPersonTooltip = labels.isUsPersonTooltip || TOOLTIP_NON_US_PERSON;

  /**
   * Notifies the caller of the new U.S.-person answer and refreshes the
   * schema so it can show or hide the citizenship follow-up questions.
   */
  const onIsUsPersonInputChange = (event: RadioChangeEvent) => {
    const { value } = event.target;
    const isUsPersonValue = value === true;

    if (onIsUsPersonChange) {
      onIsUsPersonChange(isUsPersonValue);
    }

    updateSchema();
  };

  const isUsPersonInput: Input = {
    name: "isUsPerson",
    required: true,
    type: typesMap.isUsPerson,
    label: labels.isUsPerson,
    itemProps: {
      extra: <Disclaimer text={isUsPersonTooltip} />,
    },
    inputProps: {
      /** Refreshes the schema after the U.S.-person answer changes. */
      onChange: onIsUsPersonInputChange,
    },
  };

  const hasSsnInput: Input = {
    name: "hasSsn",
    type: TYPE_BOOLEAN,
    required: true,
    label: labels.hasSsn || LABEL_HAS_SSN,
    itemProps: {
      extra: doYouHaveSsnExtra,
    },
    inputProps: {
      /** Refreshes the schema after the has-SSN answer changes. */
      onChange: () => updateSchema(),
    },
  };

  return [
    isUsPersonInput,
    ...statesInputs,
    hasSsnInput,
  ];
};

export default computeResidencyInputs;
